import {GameClientProxy} from 'com/gongxigames/math/core/client/GameClientProxy';
import {GameClientStub} from 'com/gongxigames/math/core/client/GameClientStub';
import {Session} from 'com/gongxigames/math/core/game/session/Session';
import {SlotEngine} from 'com/gongxigames/math/core/game/SlotEngine';
import {Action} from 'com/gongxigames/math/core/game/enums/Action';
import {Message} from 'com/gongxigames/math/core/game/message/Message';
import {Field} from 'com/gongxigames/math/core/game/message/Field';
import {Player} from 'com/gongxigames/math/core/game/session/Player';
import {TotalBet} from 'com/gongxigames/math/core/game/TotalBet';
import {ServerMessage} from './ServerMessage';
import {ErrorMessage} from './ErrorMessage';
import {IBuyFeatureLogic} from '@gongxigames/framework/slot/services/logic/buyFeature/IBuyFeatureLogic';
import {BuyFeatureLogicWrapper} from '@gongxigames/framework/slot/services/logic/buyFeature/BuyFeatureLogicWrapper';
import {
    GAME_CLIENT_WRAPPER_SETTINGS_PARAMETERS_SCHEMA,
    GameClientWrapperSettings, RegulationTypes
} from './GameClientWrapperSettings';
import {FlowModuleEvents} from '@gongxigames/framework/slot/modules/flow/FlowModuleEvents';
import {
    consoleLogger, CoreDI,
    IBrowser,
    ICurrencyFormatter,
    IEventManagerWithAsync, inject,
    injectable,
    ISettings,
    Installer
} from '@gongxigames/framework/core';
import {CancelStartSpinData, CancelStartSpinReason, NetworkErrors} from '@gongxigames/framework/slot/events';
import {RealityCheckEvents} from '@gongxigames/framework/slot/modules/realitycheck/RealityCheckEvents';
import {BottomPanelEvents} from '@gongxigames/framework/slot/modules/bottompanel/BottomPanelEvents';
import {SpecialWinTypeConfig} from '@gongxigames/framework/slot/configs/SpecialWinTypeConfig';
import {LOST_CONNECTION, LOW_BALANCE} from '@gongxigames/framework/slot/configs/messages';
import {PromotionsSettings, RegulationSettings} from '@gongxigames/framework/integrations/rubyplay/types';
import {KeypadControlsEvents} from '@gongxigames/framework/slot/modules/keypadControls/KeypadControlsEvents';
import {errorReporter} from '@gongxigames/framework/core/utils';
import {SignalEventEmitterWrapper} from '@gongxigames/framework/core/services/strictEventEmitter';
import {strictUiEmitter} from '@gongxigames/framework/slotV2/globals';
import {IPromotionLogicInterface} from '@gongxigames/framework/promotions/logic/interfaces/IPromotionLogicInterface';
import {NullPromotion} from '@gongxigames/framework/promotions/logic/clientImplementations/NullPromotion';
import {GameSessionPromotion, MultiSetsPromotion, SingleSetPromotion} from '@gongxigames/framework/promotions/logic/clientImplementations';

export interface IFields {
    [key: string]: any;
}
export interface GameClientWrapperProperties<S extends Session = Session, E extends SlotEngine<S>= SlotEngine<S>> {
    client: GameClientProxy<S, E> | GameClientStub<S, E>;
    callback: (response: ServerMessage) => void;
}

const LocalesWithDetailedRTP: string[] = ['nl', 'en'];
const LocalesWithoutJackpotByRegulation: Partial<Record<RegulationTypes, string[]>> = {
    german: ['de', 'en']
};

const isWithoutJackpot = (regulation: RegulationTypes, lang: string): boolean => {
    return !!LocalesWithoutJackpotByRegulation[regulation]?.includes(lang);
};

const DEFAULT_FRACTION_SIZE = 100;

@injectable()
export class GameClientWrapper<S extends Session = Session, E extends SlotEngine<S>= SlotEngine<S>> {
    public static NAME = 'offline';
    protected _callbackFn!: (response: ServerMessage) => void;
    protected _client!: GameClientProxy<S, E> | GameClientStub<S, E>;

    @inject(CoreDI.IEventManagerWithAsync)
    protected _eventManager!: IEventManagerWithAsync;
    @inject(CoreDI.Settings)
    protected _settings!: ISettings;
    @inject(CoreDI.CurrencyFormatter)
    protected _currencyFormatter!: ICurrencyFormatter;
    protected _promotionLogicInterface: IPromotionLogicInterface = new NullPromotion();
    @inject(CoreDI.Browser)
    protected _browser!: IBrowser;
    protected _buyFeatureLogic!: IBuyFeatureLogic;
    @inject(CoreDI.Installer)
    protected _installer!: Installer;
    protected emitter = new SignalEventEmitterWrapper(strictUiEmitter, {owner: this});

    protected _internetConnectionLost = false;

    public initWrapper(properties: GameClientWrapperProperties<S, E>): void {
        this._client = properties.client;
        this._callbackFn = properties.callback;

        this.setPromotionImplementation();
        this._buyFeatureLogic = new BuyFeatureLogicWrapper(this._client, this._promotionLogicInterface);

        this.initParameters();
        this.setDefaultRegulationSettings();
        this.setupEvents();
    }

    protected setPromotionImplementation(): void {
        const settings = this.getSettings<GameClientWrapperSettings>();
        const promotionIntegrationType = settings.get('promotionIntegrationImplementation');

        switch (promotionIntegrationType) {
            case 'GameSession':
                this._promotionLogicInterface = new GameSessionPromotion();
                break;
            case 'SingleSet':
                this._promotionLogicInterface = new SingleSetPromotion();
                break;
            case 'MultiSets':
                this._promotionLogicInterface = new MultiSetsPromotion();
                break;
            case 'MultiSetsMulticampaign':
                this._promotionLogicInterface = new MultiSetsPromotion();
                break;
            case 'Null':
                this._promotionLogicInterface = new NullPromotion();
                break;
            default:
                break;
        }
    }

    protected initParameters(): void {
        this._settings.addParametersSchema(GAME_CLIENT_WRAPPER_SETTINGS_PARAMETERS_SCHEMA);
        this.setCurrency();
    }

    public init(): void {
        this.handleMessage(new Message(Action.INIT));
    }

    public progressiveValues(): void {
        this.handleMessage(new Message(Action.PROGRESSIVE_VALUES));
    }

    public spin(bet: number): void {
        const promotion = this.getPromotion();

        if (promotion.isActive()) {
            bet = promotion.getBet();
            const totalBet = new TotalBet(bet, this._client.getEngine().getMaxWager());

            // update session total bet with defined in free rounds
            this.getSession().setTotalBet(totalBet);

            // add the amount of total bet to player balance,
            // math will subtract it while handling SPIN message
            this.getSession().getPlayer().addBalance(totalBet.value());
        }

        const message = this.createMessage(Action.SPIN, {[Field.BET]: bet});

        this.handleMessage(message);
    }

    public freeSpin(fields?: IFields): void {
        const message = this.createMessage(Action.FREESPIN, {});

        this.handleMessage(message);
    }

    public respin(fields?: IFields): void {
        const message = this.createMessage(Action.RESPIN, {});

        this.handleMessage(message);
    }

    public minispin(fields?: IFields): void {
        // @ts-ignore TODO: refactoring required
        const message = this.createMessage(Action.MINISPIN, {});

        this.handleMessage(message);
    }

    public pick(index: number, fields?: IFields): void {
        const message = this.createMessage(Action.PICK, {[Field.INDEX]: index});

        this.handleMessage(message);
    }

    public select(index: number, fields?: IFields): void {
        const message = this.createMessage(Action.SELECT, {[Field.INDEX]: index});

        this.handleMessage(message);
    }

    public buy(bet: number): void {
        // @ts-ignore Action.BUY is not always exists
        const message = this.createMessage(Action.BUY, {[Field.BET]: bet});

        this.handleMessage(message);
    }

    public buyFeature(bet: number): void {
        const message = this.createMessage(this._buyFeatureLogic.getAction(), {[Field.BET]: bet});

        this.handleMessage(message);
    }

    protected createMessage(action: number, fields: IFields): Message {
        const message = new Message(action);

        for (const key in fields) {
            if (fields.hasOwnProperty(key) && fields[key] !== undefined) {
                message.addField(key, fields[key]);
            }
        }

        return message;
    }

    protected onSystemInitialized(): void {
        this.setClientSettings();
    }

    protected setClientSettings(): void {
        const settings = this.getSettings<GameClientWrapperSettings>();

        if (settings.get('offlineMode')) {
            const stakes = settings.get('stakes');
            const stakeToBet = (stake: number): number => stake * this.currencyFormatter.fractionSize;
            const session = this.getSession();
            const gameSettings = session.getGameSettings();
            const setDefaultStake = (defaultStake: number): void => {
                if (defaultStake !== undefined) {
                    settings.set(
                        'defaultStake',
                        defaultStake / this.currencyFormatter.fractionSize
                    );
                }
            };

            if (stakes && stakes.length > 0) {
                gameSettings.bets = stakes.map(stakeToBet);
            }

            const {
                offlineModeBalance,
                offlineModeBetsList,
                offlineModeBetsMultiplier = 1,
                offlineModeDefaultBetIndex,
                showOddsUpfront,
                buyFeatureDisabled
            } = settings.getAllProperties();

            if (offlineModeBalance !== undefined) {
                session.getPlayer().setBalance(offlineModeBalance);
            }

            if (offlineModeBetsList !== undefined) {
                session.getGameSettings().bets = offlineModeBetsList
                    .map(Number)
                    .map((totalBet: number) => (totalBet * offlineModeBetsMultiplier) / this._client.getEngine().getMaxWager())
                    .map(stakeToBet);
            }

            if (offlineModeDefaultBetIndex !== undefined) {
                const defaultStake = session.getGameSettings().bets[offlineModeDefaultBetIndex];

                setDefaultStake(defaultStake);
            }

            if (showOddsUpfront) {
                this._eventManager.on('General.Game.ODDS_UPFRONT_ACCEPT', () => {
                    this._eventManager.emit('General.Game.ODDS_UPFRONT_ACCEPTED');
                }, this);
            }

            if (this._buyFeatureLogic.isBuyFeatureGame()) {
                settings.set('buyFeatureAvailable', !buyFeatureDisabled);
            }
        }
    }

    protected setDefaultRegulationSettings(): void {
        const settings = this.getSettings<GameClientWrapperSettings>();
        const regulationType = settings.get('regulationType')!;
        const regulationSettings: RegulationSettings = {
            regulationType
        };

        if (!settings.get('offlineMode')) {
            return;
        }

        //  TODO: This is temporary solution to make offline mode fits requirements for specific regulation.
        //   Was agreed to get them for offline mode from logic and just need to wait for the implementation
        switch (regulationType) {
            case 'spain': {
                settings.set('netPositionEnabled', true);
                settings.set('sessionEarningsEnabled', true);
                settings.set('sessionBetsEnabled', true);
                settings.set('elapsedTimeEnabled', true);
                settings.set('quickStopDisabled', true);
                settings.set('turboModeDisabled', true);
                settings.set('clock', true);
                settings.set('roundDelay', 3000);
                break;
            }
            case 'netherlands': {
                settings.set('autoSpinDisabled', true);
                settings.set('buyFeatureDisabled', true);
                break;
            }
            case 'UK': {
                settings.set('autoSpinDisabled', true);
                settings.set('turboModeDisabled', true);
                settings.set('quickStopDisabled', true);

                if (!settings.has('roundDelay')) {
                    settings.set('roundDelay', 2500);
                }

                if (!settings.has('realityCheckTimeout')) {
                    settings.set('realityCheckTimeout', 60000);
                }

                if (!settings.has('realityCheckTimeoutPassed')) {
                    settings.set('realityCheckTimeoutPassed', 0);
                }

                settings.set('ukRegulations', true);
                settings.set('changeWinToPayout', true);
                settings.set('specialWinTypeConfig', SpecialWinTypeConfig);
                settings.set('buyFeatureDisabled', true);
                settings.set('netPositionEnabled', true);
                settings.set('elapsedTimeEnabled', true);
                settings.set('clock', true);
                break;
            }
            case 'ontario': {
                settings.set('autoSpinDisabled', true);
                settings.set('roundDelay', 2500);
                settings.set('quickStopDisabled', true);
                settings.set('turboModeDisabled', true);
                settings.set('netPositionEnabled', true);
                settings.set('elapsedTimeEnabled', true);
                settings.set('changeWinToPayout', true);
                settings.set('specialWinTypeConfig', SpecialWinTypeConfig);
                settings.set('inactivityTimer', 30);
                break;
            }
            case 'colombia': {
                settings.set('roundDelay', 3000);
                settings.set('inactivityTimer', 20);
                settings.set('quickStopDisabled', true);
                settings.set('turboModeDisabled', true);
                break;
            }
            case 'german': {
                settings.set('roundDelay', 5000);
                settings.set('autoSpinDisabled', true);
                settings.set('turboModeDisabled', true);
                settings.set('quickStopDisabled', true);
                settings.set('showWinProbability', true);
                settings.set('buyFeatureDisabled', true);
                settings.set('showMaxMultiplier', true);
                settings.set('showMaxWinProbabilityOnUI', true);
                settings.set('showRTPWatermark', true);
                settings.set('clock', true);
                break;
            }
            case 'peru': {
                settings.setIfPossible('enableRTPExplanation', true);
                break;
            }
            default: {
                break;
            }
        }

        this.setRegulationSettings(regulationSettings);
    }

    protected setPromotionSettings(promotionSettings: PromotionsSettings): void {
        const settings = this.getSettings<GameClientWrapperSettings>();

        Object.entries(promotionSettings).forEach(([key, value]) => {
            const typedKey = key as keyof PromotionsSettings;

            settings.setIfPossible(typedKey, value);
        });
    }

    protected setRegulationSettings(regulationSettings: RegulationSettings): void {
        const {
            regulationType,
            delayBetweenSpins = 0,
            gameHistoryListEnabled,
            celebrationBelowBetDisabled,
            ...settingsParameters
        } = regulationSettings;
        const settings = this.getSettings<GameClientWrapperSettings>();
        const lang = settings.get('lang');

        settings.setParameters(settingsParameters);

        settings.setIfPossible('hideJackpotWords', isWithoutJackpot(regulationType, lang));
        settings.setIfPossible('regulationType', regulationType);

        if (gameHistoryListEnabled) {
            settings.setIfPossible('gameHistoryListEnabled', true);
            settings.setIfPossible('historyUrl', this.getGameHistoryListUrl());
        }

        if (delayBetweenSpins !== undefined) {
            settings.setIfPossible('roundDelay', delayBetweenSpins * 1000);
        }

        if (celebrationBelowBetDisabled) {
            settings.setIfPossible('specialWinTypeConfig', SpecialWinTypeConfig);
        }

        if (settings.get('disableClientErrorReporting')) {
            errorReporter.disable();
        }
    }

    protected getGameHistoryListUrl(): string {
        return '';
    }

    protected setupEvents(): void {
        this.on(BottomPanelEvents.SUPPORT_BUTTON_CLICKED, this.redirectToSupport);
        this.on(BottomPanelEvents.LOGOUT_BUTTON_CLICKED, this.redirectToLogout);

        strictUiEmitter.on('UI.menu.supportButtonClicked', this.redirectToSupport.bind(this));
        strictUiEmitter.on('UI.menu.logoutButtonClicked', this.redirectToLogout.bind(this));

        // redirectToHistoryUrl events
        this.on(RealityCheckEvents.GO_TO_ACCOUNT_CLICKED, this.redirectToHistoryUrl);

        // redirectToHomeUrl events
        this.on(RealityCheckEvents.QUIT_GAME_CLICKED, this.redirectToHomeUrl);
        this.on(BottomPanelEvents.HOME_BUTTON_CLICKED, this.redirectToHomeUrl);
        this.on(KeypadControlsEvents.HOME_BUTTON_CLICKED, this.redirectToHomeUrl);
        strictUiEmitter.on('UI.command.home', this.redirectToHomeUrl.bind(this));

        this.on(BottomPanelEvents.HISTORY_BUTTON_CLICKED, this.redirectToHistoryUrl);
        strictUiEmitter.on('UI.menu.historyButtonClicked', this.redirectToHistoryUrl.bind(this));

        // redirectToCashier events
        this.on(BottomPanelEvents.CASHIER_CLICKED, this.redirectToCashier);
        strictUiEmitter.on('UI.menu.cashierClicked', this.redirectToCashier.bind(this));

        this.on('General.Window.OPEN_GAME_HISTORY', this.redirectToHistoryUrl);
        this.on('General.Window.QUIT', this.quitGame);
        this.on('General.Window.GO_TO_HOME', this.redirectToHomeUrl);
        this.on('General.Window.RELOAD', this.reload);
        this.on('General.Window.OPEN_CASHIER', this.redirectToCashier);
        this.on('General.Window.OPEN_HISTORY', this.redirectToHistoryUrl);

        this.on('General.Game.SYSTEM_INITIALIZED', this.onSystemInitialized);

        this.on(FlowModuleEvents.ENTER_BASIC_STATE, this.setBets, 999999);

        this._eventManager.on('General.Flow.cancelStartSpin', this.onCancelStartSpin.bind(this), this);
        strictUiEmitter.on('General.Flow.cancelStartSpin', this.onCancelStartSpin.bind(this));

        this._eventManager.on('General.Network.SHOW_ERROR', this.onNetworkErrorShow.bind(this), this);
        this._eventManager.on('General.Network.CLOSE_ERROR', this.onNetworkErrorClose.bind(this), this);
    }

    protected onCancelStartSpin({reason}: CancelStartSpinData): void {
        if (reason === CancelStartSpinReason.INSUFFICIENT_FUNDS) {
            this._eventManager.emit('General.Message.SHOW', LOW_BALANCE);
        }
    }

    protected onNetworkErrorShow(reason: NetworkErrors): void {
        if (reason === NetworkErrors.LOST_CONNECTION) {
            this._internetConnectionLost = true;
            this._eventManager.emit('General.Message.SHOW', LOST_CONNECTION);
            this._eventManager.emitAsync('General.System.PAUSE');
        }
    }

    protected onNetworkErrorClose(reason: NetworkErrors): void {
        if (reason === NetworkErrors.LOST_CONNECTION) {
            this._internetConnectionLost = false;
            this._eventManager.emit('General.Message.CLOSE', LOST_CONNECTION);
            this._eventManager.emitAsync('General.System.RESUME');
        }
    }

    protected on(id: string | symbol, listener: Function, priority?: number): void {
        this._eventManager.on(id, listener.bind(this, id), this, priority);
    }

    protected setBets(): void {
        const sessionBets = this.getSession().getGameSettings().bets;
        const settingsBets = this.getSettings<GameClientWrapperSettings>().get('bets');

        if (this.getPromotion().isActive()) {
            return;
        }

        if (this.getSession().isFinished() && sessionBets && settingsBets && settingsBets.length > 0) {
            if (sessionBets.some((value, index) => value !== settingsBets[index])) {
                this.reload();
            }
        }
    }
    protected redirectToHistoryUrl(): void {
        const historyUrl = this.getSettings<GameClientWrapperSettings>().get('historyUrl');
        const gameHistoryListEnabled = this.getSettings<GameClientWrapperSettings>().get('gameHistoryListEnabled');

        if (!gameHistoryListEnabled && historyUrl) {
            this.openWindowTab(historyUrl);
        }
    }

    protected quitGame(): void {
        const settings = this.getSettings<GameClientWrapperSettings>();
        const redirectUrl: string = (settings.get('exitUrl') ? settings.get('exitUrl') : settings.get('homeUrl')) || '';

        this.redirect(redirectUrl);
    }

    protected redirectToHomeUrl(): void {
        const homeUrl = this.getSettings<GameClientWrapperSettings>().get('homeUrl');

        if (homeUrl) {
            const enableOpenPageInCurrentIFrame = this.getSettings<GameClientWrapperSettings>().get('enableOpenPageInCurrentIFrame');

            this.redirect(homeUrl, enableOpenPageInCurrentIFrame);
        } else {
            this.reload();
        }
    }

    protected redirectToCashier(): void {
        const enableOpenInSameTabDepositPage = this.getSettings<GameClientWrapperSettings>().get('enableOpenInSameTabDepositPage');
        const cashierUrl = this.getSettings<GameClientWrapperSettings>().get('cashierUrl')!;

        if (enableOpenInSameTabDepositPage) {
            this.redirect(cashierUrl);
        } else {
            this.openWindowTab(cashierUrl);
        }
    }

    protected redirectToSupport(): void {
        this.openWindowTab(this.getSettings<GameClientWrapperSettings>().get('supportUrl')!);
    }

    protected redirectToLogout(): void {
        this.redirect(this.getSettings<GameClientWrapperSettings>().get('logoutUrl')!);
    }

    protected redirect(url: string, enableOpenPageInCurrentIFrame?: boolean): void {
        if (url !== '') {
            if (enableOpenPageInCurrentIFrame) {
                window.location.href = decodeURI(url);
            } else if (window.top) {
                window.top!.location.href = decodeURI(url);
            }
        } else {
            consoleLogger.error('redirect invalid url parameter');
        }
    }

    protected openWindowTab(url: string): void {
        if (url !== '') {
            let newTab;

            try {
                newTab = (window.parent || window).open(url, '_blank');
            } catch (e) {
                newTab = window.open(url, '_blank');
            }

            if (newTab) {
                newTab.focus();
            }
        } else {
            consoleLogger.error('openWindowTab invalid url parameter');
        }
    }

    protected reload(): void {
        location.reload();
    }

    protected handleMessage(msg: Message): void {
        if (this._client instanceof GameClientProxy) {
            try {
                // prepare logic request
                const request = this._client.prepareRequest(msg);

                // Some games math required bets array for calculations(ex. DragonLadies)
                // Without bets array Game History won't work correctly
                if (this.getSession() && this.getSession().getGameSettings()) {
                    request.addField('bets', this.getSession().getGameSettings().bets);
                }

                // send the request to logic
                // it's response will be handled asynchronously
                this.sendToServer(request);
            } catch (e) {
                this.onException(e);
            }
        } else if (this._client instanceof GameClientStub) {
            // handle the request message in place and
            // send the response straight-away to the clint

            // TODO: fix in math
            // subtract the total bet amount in this frame
            // and add the same just before sending the request to math in the next frame
            // it is done in fun-mode to properly show player balance without bet amount
            // must be fixed in math, GameClientStub should handle the actions asynchronously
            const bet = msg.getField(Field.BET) as number;
            const totalBet: number = bet * this._client.getEngine().getMaxWager();
            const player: Player = this.getSession().getPlayer();

            if (this._buyFeatureLogic.isAction(msg.getAction())) {
                player.addBalance(-this._buyFeatureLogic.getPrice(bet));
            }

            if (msg.getAction() === Action.SPIN) {

                player.addBalance(-totalBet);
            }

            this._browser.addTimeout(() => {
                if (this._buyFeatureLogic.isAction(msg.getAction())) {
                    player.addBalance(this._buyFeatureLogic.getPrice(bet));
                }

                if (msg.getAction() === Action.SPIN) {
                    player.addBalance(totalBet);
                }

                this.onServerResponse((this._client as GameClientStub<S, E>).handle(msg));
            }, 1);
        }
    }

    protected onException(e: any): void {
        const msg: ServerMessage = new ServerMessage();
        const data: ErrorMessage = {
            ErrMessageID: 'internalError'
        };

        msg.topic = ServerMessage.TOPIC_GAMESERVER_ERROR;
        msg.data = data;
        this.callBack(msg);

        consoleLogger.error(e);
    }

    protected sendToServer(request: Message): void {
        throw new Error('Not implemented');
    }

    protected onServerResponse(response: Message): void {
        if (this._client instanceof GameClientProxy) {
            try {
                response = this._client.handleResponse(response);
            } catch (e) {
                this.onException(e);
            }
        }

        const serverMessage: ServerMessage = new ServerMessage();

        serverMessage.topic = ServerMessage.TOPIC_GAMESERVER;
        serverMessage.data = response;
        this.callBack(serverMessage);
    }

    protected callBack(response: ServerMessage): void {
        this._callbackFn(response);
    }

    public responseHandle(response: ServerMessage): void {

    }

    public getSettings<Settings extends ISettings>(): Settings {
        return this._settings as Settings;
    }

    public get currencyFormatter(): ICurrencyFormatter {
        return this._currencyFormatter;
    }

    public get wager(): number {
        return this._client.getEngine().getMaxWager();
    }

    public getPromotion<PromotionLogicInterface extends IPromotionLogicInterface>(): PromotionLogicInterface {
        return this._promotionLogicInterface as PromotionLogicInterface;
    }

    /**
     * Returns <tt>true</tt> if player is allowed to start a spin with <b>bet</b> amount, <tt>false</tt> otherwise.
     * <p>
     * First, all promotion checks are done. The method will return <tt>true</tt> in case of an active
     * unfinished promotion. <b>NOTE:</b> when playing a promotion, math does not use a bet set in session.
     * Therefore this method will not use <b>bet</b> parameter. Instead it uses bet, configured in the promotion.
     * <p>
     * If there is no any active unfinished promotion, method will return <tt>true</tt> only if
     * player has enough balance to place a <b>bet</b>.
     * <p>
     * @param bet total bet amount to place and start a spin with
     *
     * @returns <tt>true</tt> if spin can be started, <tt>false</tt> otherwise
     */
    public canSpin(bet: number): boolean {
        if (this.getPromotion().isActive()) {
            return true;
        }

        return bet <= this.getSession().getPlayer().getBalance();
    }

    public getBets(): number[] {
        if (this.getPromotion().isActive()) {
            return [this.getPromotion().getBet()];
        }

        return this.getSession().getGameSettings().bets;
    }

    public getBalance(): number {
        return this._client.getSession().getPlayer().getBalance();
    }

    /**
     * return first value. Override if needed
     */
    public getDefaultBet(): number {
        const bets = this.getBets();
        const defaultStake = this.getSettings<GameClientWrapperSettings>().get('defaultStake');

        if (defaultStake && bets.indexOf(defaultStake * this.currencyFormatter.fractionSize) > -1) {
            return defaultStake * this.currencyFormatter.fractionSize;
        }

        return bets[0];
    }

    public getSession(): Session {
        return this._client.getSession();
    }

    public get client(): GameClientProxy<S, E> {
        return this._client as GameClientProxy<S, E>;
    }

    protected isInitAction(action?: Action): boolean {
        return action === Action.INIT;
    }

    private setCurrency(): void {
        const {
            currency,
            lang
        } = this.getSettings<GameClientWrapperSettings>().getAllProperties();
        const currencyConfig = this.currencyFormatter.getCurrencyData(currency);

        if (!currencyConfig) {
            const {symbol, uniqSymbol, ...other} = this.currencyFormatter.getCurrencyData('EUR')!;

            this.currencyFormatter.setCurrencyData(currency, {
                ...other,
                name: currency,
                symbol: null,
                uniqSymbol: null
            });
        }

        this.currencyFormatter.setParameters({
            currencyCode: currency,
            fractionSize: DEFAULT_FRACTION_SIZE,
            localeCode: lang
        });
    }

}
